import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import useBetEditState from '../../../../../hooks/useBetEditState';
import { PlusIcon, FootballIcon, VolleyballIcon, TeamsIcon } from '../../../model/icons';
import GameHeader from '../GameHeader';
import StatusRow from '../StatusRow';
import SportEventCard from './SportEventCard';
import ActivityEventCard from './ActivityEventCard';
import PlayerRow from './PlayerRow';
import TeamsModal from './TeamsModal';
import CreateSportModal from './modals/CreateSportModal';
import FinishSportModal from './modals/FinishSportModal';
import CreateActivityModal from './modals/CreateActivityModal';
import FinishActivityModal from './modals/FinishActivityModal';
import '../games.css';
import './bets.css';

const MAIN_TABS = [
    { key: 'sport', label: 'Спортивные события' },
    { key: 'mero', label: 'Мероприятия' },
    { key: 'players', label: 'Игроки' }
];

const SPORT_KINDS = [
    { key: 'FOOTBALL', label: 'Футбол', Icon: FootballIcon },
    { key: 'VOLLEYBALL', label: 'Волейбол', Icon: VolleyballIcon }
];

// 4 статуса игры (бэк: GameStatus = DEVELOP/WAITING_ACTIVE/ACTIVE/FINISHED, без ограничений переходов).
const BET_STATUSES = [
    { value: 'DEVELOP', label: 'Разработка', tone: 'dev' },
    { value: 'WAITING_ACTIVE', label: 'Ожидание', tone: 'wait' },
    { value: 'ACTIVE', label: 'Активна', tone: 'active' },
    { value: 'FINISHED', label: 'Завершена', tone: 'done' }
];

// Игра «Ставки»: head-card (инфо + статус) + табы спорт/мероприятия.
export default function BetsGame({ shift }) {
    const bet = useBetEditState();
    const { enqueueSnackbar } = useSnackbar();
    const { show: showPreloader, hide: hidePreloader } = usePreloaderStore();

    const [mainTab, setMainTab] = useState('sport');
    const [sportKind, setSportKind] = useState('FOOTBALL');
    const [createSport, setCreateSport] = useState(false);
    const [createMero, setCreateMero] = useState(false);
    const [editSport, setEditSport] = useState(null);
    const [editMero, setEditMero] = useState(null);
    const [finishSportId, setFinishSportId] = useState(null);
    const [finishMeroId, setFinishMeroId] = useState(null);
    const [teamsOpen, setTeamsOpen] = useState(false);

    const colorOf = useMemo(() => {
        const map = new Map();
        bet.teams.forEach(t => map.set(t.teamId, t.color));
        return (teamId) => map.get(teamId);
    }, [bet.teams]);

    if (bet.gameError) {
        const message = bet.gameError.response?.data?.message || 'Ошибка загрузки игры «Ставки»';
        return <div className="vp-empty">{message}</div>;
    }
    if (bet.isLoading || !bet.gameData) return <div className="vp-empty">Загрузка…</div>;

    const changeStatus = async (newStatus) => {
        if (newStatus === bet.gameData.status) return;
        showPreloader();
        try {
            await apiClient.patch(`/api/bet/admin/games/update-status?id=${bet.gameId}&status=${newStatus}`);
            bet.invalidateGame();
            enqueueSnackbar('Статус обновлён', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar(e.response?.data?.message || 'Не удалось сменить статус', { variant: 'error' });
        } finally {
            hidePreloader();
        }
    };

    const confirmDeleteSport = (id) => {
        if (window.confirm('Удалить событие? Все активные ставки будут возвращены игрокам, им придёт уведомление.')) bet.deleteEvent(id);
    };
    const confirmDeleteMero = (id) => {
        if (window.confirm('Удалить мероприятие? Все активные ставки будут возвращены игрокам, им придёт уведомление.')) bet.deleteActivityEvent(id);
    };

    const meta = (
        <>
            <span><b>{bet.teams.length}</b> команд</span>
            <span className="dot">·</span>
            <span><b>{bet.events.length + bet.activityEvents.length}</b> событий</span>
            <span className="dot">·</span>
            <span><b>{bet.players.length}</b> игроков</span>
            <span className="dot">·</span>
            <span><b>{bet.bets.length + bet.activityBets.length}</b> ставок</span>
        </>
    );

    const finishSportEvent = finishSportId != null ? bet.getEventById(finishSportId) : null;
    const finishMeroEvent = finishMeroId != null ? bet.getActivityEventById(finishMeroId) : null;

    return (
        <>
            <GameHeader
                title={`Ставки — ${shift.title || ''}`}
                meta={meta}
                status={<StatusRow statuses={BET_STATUSES} current={bet.gameData.status} onChange={changeStatus} />}
            />
            <section className="content white">
                <div className="bt-tabs">
                    {MAIN_TABS.map(t => (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a key={t.key} className={mainTab === t.key ? 'on' : ''} onClick={() => setMainTab(t.key)}>{t.label}</a>
                    ))}
                </div>

                {mainTab === 'sport' && (
                    <div className="bt-panel">
                        <div className="ev-head">
                            <h2>Спортивные события</h2>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button
                                    type="button"
                                    className={`btn ${bet.teams.length === 0 ? 'btn-warn' : 'btn-ghost'}`}
                                    onClick={() => setTeamsOpen(true)}
                                >
                                    <span className="ic"><TeamsIcon /></span>Команды ({bet.teams.length})
                                </button>
                                <button type="button" className="btn btn-primary" onClick={() => setCreateSport(true)}>
                                    <span className="ic"><PlusIcon /></span>Создать событие
                                </button>
                            </div>
                        </div>
                        <div className="bt-sport">
                            {SPORT_KINDS.map(({ key, label, Icon }) => {
                                const cur = bet.currentEvents.filter(e => e.eventType === key).length;
                                const fin = bet.finishedEvents.filter(e => e.eventType === key).length;
                                return (
                                    <button type="button" key={key} className={`bt-sp${sportKind === key ? ' on' : ''}`} onClick={() => setSportKind(key)}>
                                        <Icon />{label}<span className="c">({cur}/{fin})</span>
                                    </button>
                                );
                            })}
                        </div>

                        <SportGroups
                            kind={sportKind}
                            current={bet.currentEvents.filter(e => e.eventType === sportKind)}
                            finished={bet.finishedEvents.filter(e => e.eventType === sportKind)}
                            getBets={bet.getBetsByEventId}
                            colorOf={colorOf}
                            onEdit={setEditSport}
                            onFinish={setFinishSportId}
                            onDelete={confirmDeleteSport}
                        />
                    </div>
                )}

                {mainTab === 'mero' && (
                    <div className="bt-panel">
                        <div className="ev-head">
                            <h2>Мероприятия</h2>
                            <button type="button" className="btn btn-primary" onClick={() => setCreateMero(true)}>
                                <span className="ic"><PlusIcon /></span>Создать мероприятие
                            </button>
                        </div>
                        <MeroGroups
                            current={bet.currentActivityEvents}
                            finished={bet.finishedActivityEvents}
                            getBets={bet.getActivityBetsByEventId}
                            colorOf={colorOf}
                            onEdit={setEditMero}
                            onFinish={setFinishMeroId}
                            onDelete={confirmDeleteMero}
                        />
                    </div>
                )}

                {mainTab === 'players' && (
                    <div className="bt-panel">
                        <div className="ev-head">
                            <h2>Игроки <span className="fr-cnt">({bet.playerBets.length})</span></h2>
                        </div>
                        {bet.playerBetsLoading ? (
                            <div className="bt-empty">Загрузка…</div>
                        ) : bet.playerBets.length > 0 ? (
                            <div className="bt-players">
                                {bet.playerBets.map((player, index) => (
                                    <PlayerRow key={player.userId} player={player} index={index} />
                                ))}
                            </div>
                        ) : <div className="bt-empty">Нет игроков</div>}
                    </div>
                )}
            </section>

            {createSport && (
                <CreateSportModal teams={bet.teams} onSubmit={bet.createEvent} onClose={() => setCreateSport(false)} />
            )}
            {editSport && (
                <CreateSportModal
                    teams={bet.teams}
                    event={editSport}
                    onSubmit={(data) => bet.updateEvent(editSport.id, data)}
                    onClose={() => setEditSport(null)}
                />
            )}
            {finishSportEvent && (
                <FinishSportModal event={finishSportEvent} onSubmit={bet.finishEvent} onClose={() => setFinishSportId(null)} />
            )}
            {createMero && (
                <CreateActivityModal onSubmit={bet.createActivityEvent} onClose={() => setCreateMero(false)} />
            )}
            {editMero && (
                <CreateActivityModal
                    event={editMero}
                    onSubmit={(data) => bet.updateActivityEvent(editMero.id, data)}
                    onClose={() => setEditMero(null)}
                />
            )}
            {finishMeroEvent && (
                <FinishActivityModal event={finishMeroEvent} colorOf={colorOf} onSubmit={bet.finishActivityEvent} onClose={() => setFinishMeroId(null)} />
            )}
            {teamsOpen && (
                <TeamsModal teams={bet.teams} onRefresh={bet.refreshTeams} onDelete={bet.deleteTeam} onClose={() => setTeamsOpen(false)} />
            )}
        </>
    );
}

function SportGroups({ kind, current, finished, getBets, colorOf, onEdit, onFinish, onDelete }) {
    const kindLabel = SPORT_KINDS.find(k => k.key === kind)?.label || '';
    return (
        <>
            <div className="bt-grouplabel">Текущие события</div>
            {current.length > 0 ? (
                <div className="bt-evgrid">
                    {current.map(ev => (
                        <SportEventCard key={ev.id} event={ev} bets={getBets(ev.id)} colorOf={colorOf}
                            onEdit={() => onEdit(ev)} onFinish={() => onFinish(ev.id)} onDelete={() => onDelete(ev.id)} />
                    ))}
                </div>
            ) : <div className="bt-empty">Нет текущих событий ({kindLabel})</div>}

            <div className="bt-grouplabel mt">Завершённые события</div>
            {finished.length > 0 ? (
                <div className="bt-evgrid">
                    {finished.map(ev => (
                        <SportEventCard key={ev.id} event={ev} bets={getBets(ev.id)} colorOf={colorOf} finished />
                    ))}
                </div>
            ) : <div className="bt-empty">Нет завершённых событий ({kindLabel})</div>}
        </>
    );
}

SportGroups.propTypes = {
    kind: PropTypes.string.isRequired,
    current: PropTypes.array.isRequired,
    finished: PropTypes.array.isRequired,
    getBets: PropTypes.func.isRequired,
    colorOf: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onFinish: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

function MeroGroups({ current, finished, getBets, colorOf, onEdit, onFinish, onDelete }) {
    return (
        <>
            <div className="bt-grouplabel">Текущие мероприятия</div>
            {current.length > 0 ? (
                <div className="bt-evgrid">
                    {current.map(ev => (
                        <ActivityEventCard key={ev.id} event={ev} bets={getBets(ev.id)} colorOf={colorOf}
                            onEdit={() => onEdit(ev)} onFinish={() => onFinish(ev.id)} onDelete={() => onDelete(ev.id)} />
                    ))}
                </div>
            ) : <div className="bt-empty">Нет текущих мероприятий</div>}

            <div className="bt-grouplabel mt">Завершённые мероприятия</div>
            {finished.length > 0 ? (
                <div className="bt-evgrid">
                    {finished.map(ev => (
                        <ActivityEventCard key={ev.id} event={ev} bets={getBets(ev.id)} colorOf={colorOf} finished />
                    ))}
                </div>
            ) : <div className="bt-empty">Нет завершённых мероприятий</div>}
        </>
    );
}

MeroGroups.propTypes = {
    current: PropTypes.array.isRequired,
    finished: PropTypes.array.isRequired,
    getBets: PropTypes.func.isRequired,
    colorOf: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onFinish: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired
};

BetsGame.propTypes = {
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};
